import React, { useState, useEffect, forwardRef } from 'react';
import {
  TextField, Button, Box, Grid, IconButton, Select, MenuItem, InputLabel, FormControl, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Slide, Divider, FormHelperText
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { TransitionProps } from '@mui/material/transitions';
import AvatarImage from '../../../../Navbar/AvatarImage';
import { validateEmail, validateUsername } from '../../../Validation/validation';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'; import { theme } from '../../../../../theme'; // Assuming the theme import is used globally
import imageCompression from 'browser-image-compression';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface UserProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (updatedUser: any) => void; // Passes the updated user data back to the parent
  userData: {
    UserID: string;
    UserName: string;
    EmailID: string;
    UserRole: string;
    UserStatus: string;
    UserNum: number;
    ImageData: string;
    MIMEType: string // URL for photo preview
  } | null;
}

const EditUser: React.FC<UserProps> = ({ open, onClose, onSubmit, userData }) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    userid: '',
    username: '',
    email: '',
    role: '',
    status: 'Active',
    usernum: 0,
    photo: null as File | null,
    photoURL: '', // URL for photo preview
  });

  useEffect(() => {
    if (userData) {
      const base64Preview = userData.ImageData
        ? `data:${userData.MIMEType};base64,${userData.ImageData}`
        : '';
      setFormData({
        userid: userData.UserID || '',
        username: userData.UserName || '',  // Fallback to empty string
        email: userData.EmailID || '',
        role: userData.UserRole || '',
        status: userData.UserStatus || 'Active',  // Fallback to 'Active',
        usernum: userData.UserNum || 0,
        photo: null,
        photoURL: base64Preview
      });
    } else {
      // Reset to default values if userData is null
      setFormData({
        userid: '',
        username: '',
        email: '',
        role: '',
        status: 'Active',
        usernum: 0,
        photo: null,
        photoURL: ''
      });
    }
  }, [userData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [event.target.name]: ''
    }))
  };
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Compress
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.05, // 0.05 MB = 50 KB
        maxWidthOrHeight: 300,
        useWebWorker: true,
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          photo: compressedFile,
          photoURL: reader.result as string,
        }));
      };
      reader.readAsDataURL(compressedFile);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      role: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      role: ''
    }))
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      status: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      status: ''
    }))
  };
  const handleAvatarClick = () => {
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleSubmit = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required.';
    }
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.role.trim()) {
      newErrors.role = 'Role is required.';
    }
    if (!formData.status.trim()) {
      newErrors.status = 'Status is required.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return
    }
    onSubmit(formData);
    setFormData({
      userid: '',
      username: '',
      email: '',
      role: '',
      status: 'Active',
      usernum: 0,
      photo: null,
      photoURL: ''
    });
    onClose()
  };

  return (
    <Dialog
      open={open}
      onClose={() => { }}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)", } }}
      PaperProps={{
        sx: {
          position: 'absolute',
          width: "22vw",
          right: 0,

        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: '1.2vw 1vw 0 1vw',
          minHeight: '4vw',
          width: '100%',
        }}
      >
        {/* Left Section: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonAddAltOutlinedIcon
            sx={{
              border: '0.0694vw solid #EEEEEE',
              bgcolor: theme.palette.secondary.light,
              color: theme.palette.secondary.main,
              borderRadius: '0.56vw',
              width: '2.5vw',
              height: '2.5vw',
              p: '0.2vw',
              mr: '1.11vw',
            }}
          />
          <Box>
            <Typography variant="h6" fontWeight={600}>Edit User</Typography>
            {/* Optional subtitle */}
            {/* <Typography variant="body2">Update the user information below</Typography> */}
          </Box>
        </Box>

        {/* Right Section: Close Button */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            padding: '0.3vw',
            mt: '0.1vw',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <Divider sx={{ mb: 1.2, mt: 2 }} />

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography gutterBottom variant='subtitle2'>Profile picture</Typography>
            <IconButton onClick={handleAvatarClick}>


              {formData.photoURL ? (
                <AvatarImage
                  imageData={formData.photoURL.replace(/^data:image\/\w+;base64,/, "")}
                  Mimetype={formData.photoURL?.match(/^data:(image\/\w+);base64/)?.[1] || "image/jpeg"}
                  height="40px"
                  width="40px" />
              ) : userData?.UserNum ? (
                <AvatarImage imageData={userData.ImageData} Mimetype={userData.MIMEType} height="40px" width="40px" />
              ) : (
                <AccountCircleOutlinedIcon sx={{ fontSize: '40px' }} />
              )}

            </IconButton>
            <Divider sx={{ mb: 1.2, mt: 2 }} />
          </Grid>

          <Grid item xs={12} mb={'0.8vw'}>
            <Typography variant="body2" color="textSecondary">
              <b>UserID: {formData.userid}</b>
            </Typography>
          </Grid>

          <Grid item xs={12} mb={'0.8vw'}>
            <TextField
              size="small"
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              variant="outlined"
              error={!!errors.username}
              helperText={errors.username}
              autoComplete='off'
              required
            />
          </Grid>

          <Grid item xs={12} mb={'0.8vw'}>
            <TextField
              size="small"
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => {
                if (formData.email && !validateEmail(formData.email)) {
                  setErrors((prev) => ({ ...prev, email: 'Invalid email address' }));
                }
              }}
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email}
              autoComplete='off'
              required
            />
          </Grid>


          <Grid item xs={12} mb={'0.8vw'}>
            <FormControl fullWidth size="small" required error={!!errors.role}>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} onChange={handleRoleChange} label="Role">
                <MenuItem value="HR">HR</MenuItem>
                <MenuItem value="Recruiter">Recruiter</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Super Admin">Super Admin</MenuItem>
              </Select>
              {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} mb={'0.8vw'}>
            <FormControl fullWidth size="small" required error={!!errors.status}>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={handleStatusChange} label="Status">
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="In-Active">In-Active</MenuItem>
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
        <input
          id="file-upload"
          type="file"
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileChange}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ padding: '1.2vw 1vw' }}>
        <Button onClick={handleSubmit} variant="contained" color="primary" fullWidth >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUser;
